import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ProductCartCounter from '../components/ProductCartCounter';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { persistProductCart, persistTreatmentCart } from '../utils/cartPersistence';
import '../styles/PageBackground.css';
import './ProductRecommendationsPage.css';
import './TreatmentRecommendationsPage.css';

const DEFAULT_TREATMENT_SUGGEST_LIMIT = 8;
const TREATMENT_RECOMMENDATIONS_CACHE_PREFIX = 'salon_treatment_recommendations_';

const hasTreatmentRecommendations = (payload) =>
  Boolean(payload && Array.isArray(payload.recommended_treatments));

/** Position fixed panel to sit on top of the treatment card (viewport coords). */
const computeTreatmentUspCardLayout = (rect) => {
  if (!rect || typeof window === 'undefined') return null;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rect.left;
  let width = rect.width;
  if (width > vw - 2 * pad) {
    width = Math.max(vw - 2 * pad, 240);
    left = pad;
  } else {
    left = Math.max(pad, Math.min(left, vw - width - pad));
  }
  const top = Math.max(pad, Math.min(rect.top, vh - pad - 120));
  const maxHeight = Math.max(160, Math.min(vh * 0.88, vh - top - pad));
  const minHeight = Math.min(Math.max(rect.height, 120), maxHeight);
  return { top, left, width, minHeight, maxHeight };
};

/** Stable cart key for a treatment row (aligned with CheckoutModal treatment map). */
const treatmentRowCartKey = (item, idx) => {
  if (item?.id != null) return `treatment_${item.id}`;
  if (item?.treatment_id != null) return `treatment_${item.treatment_id}`;
  const stableName = String(item?.treatment || item?.name || '').trim();
  if (stableName) return `treatment_${stableName.replace(/\s+/g, '_').toLowerCase()}`;
  return `treatment_${idx}_row`;
};

const TreatmentRecommendationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (
      location.state &&
      (location.state.skinResults || location.state.routineData || location.state.treatmentCartItems != null)
    ) {
      return location.state;
    }
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;

  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const visibleConditionNames = effectiveState?.visibleConditionNames ?? location.state?.visibleConditionNames ?? null;

  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () =>
      effectiveState?.treatmentCartItems && typeof effectiveState.treatmentCartItems === 'object'
        ? effectiveState.treatmentCartItems
        : {}
  );
  const [productCartItems, setProductCartItems] = useState(
    () => (cartItemsFromState && typeof cartItemsFromState === 'object' ? cartItemsFromState : {})
  );
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [uspPopup, setUspPopup] = useState({
    open: false,
    text: '',
    heading: '',
    imageUrl: '',
    sections: [],
    anchorCardId: '',
    anchorRect: null,
  });

  const [treatmentSuggestLimit, setTreatmentSuggestLimit] = useState(null);
  const [serviceSuggestionEnabled, setServiceSuggestionEnabled] = useState(true);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    if (!token) {
      setTreatmentSuggestLimit(DEFAULT_TREATMENT_SUGGEST_LIMIT);
      setServiceSuggestionEnabled(true);
      setProductSuggestionEnabled(true);
      return;
    }
    let cancelled = false;
    fetchWithTimeout(`${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/suggest-product`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const service = String(data?.service_suggestion || '').trim().toLowerCase();
        const product = String(data?.product_suggestion || '').trim().toLowerCase();

        const serviceEnabled = service === 'yes';
        setServiceSuggestionEnabled(serviceEnabled);
        setProductSuggestionEnabled(product === 'yes');

        if (!serviceEnabled) {
          navigate(pathWithSessionId('/product-recommendations', sessionId), {
            state: {
              capturedImage: capturedImageFromState,
              userData: userDataFromState,
              sessionId,
              skinResults: skinResultsFromState,
              routineData: routineDataFromState,
              aqiData: aqiDataFromState,
              treatmentCartItems,
              visibleConditionNames,
              cartItems: productCartItems,
              ...(token && { token }),
              treatmentSkipped: true,
            },
            replace: true,
          });
          return;
        }

        const limit = data?.product_suggest_limit;
        const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
        setTreatmentSuggestLimit(Number.isNaN(num) || num < 1 ? DEFAULT_TREATMENT_SUGGEST_LIMIT : num);

        const orgId = data?.organization_id;
        setOrganizationId(orgId != null && orgId !== '' ? String(orgId) : null);
      })
      .catch(() => {
        if (!cancelled) {
          setTreatmentSuggestLimit(DEFAULT_TREATMENT_SUGGEST_LIMIT);
          setServiceSuggestionEnabled(true);
          setProductSuggestionEnabled(true);
        }
      });
    return () => { cancelled = true; };
  }, [
    token,
    navigate,
    sessionId,
    capturedImageFromState,
    userDataFromState,
    skinResultsFromState,
    routineDataFromState,
    aqiDataFromState,
    treatmentCartItems,
    visibleConditionNames,
    productCartItems,
  ]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const isVercelBlobUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      return (
        u.protocol === 'https:' &&
        (host === 'blob.vercel-storage.com' ||
          host.endsWith('.blob.vercel-storage.com') ||
          host === 'public.blob.vercel-storage.com' ||
          host.endsWith('.public.blob.vercel-storage.com'))
      );
    } catch {
      return false;
    }
  };

  const resolveTreatmentImageUrl = (url) => {
    if (!url) return url;
    let raw = url;
    if (Array.isArray(raw)) raw = raw[0];
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed[0]) raw = parsed[0];
      } catch {
        // ignore
      }
    }
    if (!raw || typeof raw !== 'string') return null;
    if (!isVercelBlobUrl(raw)) return raw;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return `${base}${SALON_API_PREFIX}/blob-proxy?url=${encodeURIComponent(raw)}`;
  };

  const persistRecommendations = useCallback((data) => {
    if (!sessionId || !data || typeof data !== 'object') return;
    try {
      localStorage.setItem(`${TREATMENT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`, JSON.stringify(data));
    } catch (e) {
      // ignore cache write issues
    }
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    saveSkinFlowState(sessionId, {
      ...current,
      ...fromLocation,
      sessionId,
      treatmentRecommendations: data,
      treatmentCartItems,
      cartItems: productCartItems,
    });
  }, [sessionId, location.state, treatmentCartItems, productCartItems]);

  const handleCloseUspPopup = () => {
    setUspPopup({
      open: false,
      text: '',
      heading: '',
      imageUrl: '',
      sections: [],
      anchorCardId: '',
      anchorRect: null,
    });
  };

  const buildTreatmentDetailSections = (item) => {
    const sections = [];
    const push = (label, value) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        const list = value.map((v) => String(v || '').trim()).filter(Boolean);
        if (!list.length) return;
        sections.push({ label, value: list.join(', '), list });
        return;
      }
      const text = String(value).trim();
      if (!text) return;
      sections.push({ label, value: text });
    };
    push('About', item?.about_treatment || item?.description);
    push('Key benefit', item?.major_usp);
    push('Kit / Product', item?.kit);
    push('Core ingredients', item?.core_ingredients);
    push('Suggested post care', item?.post_care);
    push('Rate card', item?.rate_card);
    push('Brand', item?.brand);
    push('Addresses', item?.conditions);
    push('Goals', item?.skin_goals);
    const priceBits = [];
    if (item?.net_price != null && item?.net_price !== '') priceBits.push(`₹${item.net_price}`);
    if (item?.sale_price != null && item?.sale_price !== '' && String(item.sale_price) !== String(item.net_price)) {
      priceBits.push(`sale ₹${item.sale_price}`);
    }
    if (item?.price && !priceBits.length) priceBits.push(String(item.price));
    if (priceBits.length) push('Price', priceBits.join(' · '));
    return sections;
  };

  const openTreatmentDetails = (item, cartKey, event) => {
    event?.stopPropagation?.();
    const cardEl =
      event?.currentTarget?.closest?.('[data-treatment-usp-anchor]') ||
      document.querySelector(`[data-treatment-usp-anchor="${CSS.escape(cartKey)}"]`);
    const rect = cardEl ? cardEl.getBoundingClientRect() : null;
    const sections = buildTreatmentDetailSections(item);
    const fallbackText =
      item?.about_treatment || item?.description || item?.major_usp || 'No additional details available.';
    setUspPopup({
      open: true,
      text: fallbackText,
      heading: item?.treatment || item?.name || 'Treatment details',
      imageUrl: item?.image_url ? resolveTreatmentImageUrl(item.image_url) : '',
      sections,
      anchorCardId: cartKey,
      anchorRect: rect,
    });
  };

  useEffect(() => {
    if (!uspPopup.open || !uspPopup.anchorCardId) return undefined;
    const sel = `[data-treatment-usp-anchor="${CSS.escape(uspPopup.anchorCardId)}"]`;
    const el = document.querySelector(sel);
    if (!el) return undefined;
    const sync = () => {
      const next = el.getBoundingClientRect();
      if (next.width <= 0 || next.height <= 0) return;
      setUspPopup((p) => {
        if (!p.open) return p;
        return {
          ...p,
          anchorRect: {
            top: next.top,
            left: next.left,
            width: next.width,
            height: next.height,
          },
        };
      });
    };
    sync();
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [uspPopup.open, uspPopup.anchorCardId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        uspPopup.open &&
        !e.target.closest('.treatment-about-modal__panel') &&
        !e.target.closest('.product-usp')
      ) {
        handleCloseUspPopup();
      }
    };
    if (uspPopup.open) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [uspPopup.open]);

  useEffect(() => {
    if (!sessionId) return;
    const fromState =
      effectiveState?.treatmentRecommendations
      ?? location.state?.treatmentRecommendations
      ?? (hasTreatmentRecommendations(effectiveState?.recommendations) ? effectiveState?.recommendations : null)
      ?? (hasTreatmentRecommendations(location.state?.recommendations) ? location.state?.recommendations : null)
      ?? null;

    if (hasTreatmentRecommendations(fromState)) {
      setRecommendations(fromState);
      setIsLoading(false);
      return;
    }
    try {
      const cached = localStorage.getItem(`${TREATMENT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          setRecommendations(parsed);
          setIsLoading(false);
        }
      }
    } catch (e) {
      // ignore cache read issues
    }
  }, [
    sessionId,
    effectiveState?.treatmentRecommendations,
    location.state?.treatmentRecommendations,
    effectiveState?.recommendations,
    location.state?.recommendations,
  ]);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    saveSkinFlowState(sessionId, {
      ...current,
      ...fromLocation,
      sessionId,
      treatmentCartItems,
      cartItems: productCartItems,
    });
  }, [sessionId, treatmentCartItems, productCartItems, location.state]);

  const saveTreatmentCartToBackend = useCallback(
    (items) => {
      if (!API_BASE_URL || !sessionId) return;
      persistTreatmentCart(API_BASE_URL, sessionId, token, items).then((r) => {
        if (r && !r.ok) console.warn('Could not save treatment cart');
      });
    },
    [API_BASE_URL, sessionId, token]
  );

  const saveProductCartToBackend = useCallback(
    (items) => {
      if (!API_BASE_URL || !sessionId) return;
      persistProductCart(API_BASE_URL, sessionId, token, items).then((r) => {
        if (r && !r.ok) console.warn('Could not save product cart');
      });
    },
    [API_BASE_URL, sessionId, token]
  );

  useEffect(() => {
    saveTreatmentCartToBackend(treatmentCartItems);
  }, [treatmentCartItems, saveTreatmentCartToBackend]);

  useEffect(() => {
    saveProductCartToBackend(productCartItems);
  }, [productCartItems, saveProductCartToBackend]);

  useEffect(() => {
    if (treatmentSuggestLimit == null) return;
    if (hasTreatmentRecommendations(recommendations)) {
      setIsLoading(false);
      return;
    }

    const fetchTreatments = async () => {
      if (!skinResultsFromState?.skinScore) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      const limit =
        typeof treatmentSuggestLimit === 'number' && treatmentSuggestLimit >= 1
          ? treatmentSuggestLimit
          : DEFAULT_TREATMENT_SUGGEST_LIMIT;

      const applyCartFromStored = (storedData) => {
        const rows = storedData?.cart_added;
        if (!Array.isArray(rows) || rows.length === 0) return;
        const next = {};
        rows.forEach((row, i) => {
          const t = row.treatment;
          const q = row.quantity;
          if (!t || !q || q <= 0) return;
          const key = treatmentRowCartKey(t, i);
          next[key] = { treatment: t, quantity: q };
        });
        if (Object.keys(next).length > 0) setTreatmentCartItems(next);
      };

      try {
        if (sessionId) {
          try {
            const storedParams = new URLSearchParams();
            if (token) storedParams.set('app_session_token', token);
            if (organizationId) storedParams.set('organization_id', organizationId);
            const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
            const storedRes = await fetchWithTimeout(
              `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-treatments/${sessionId}${storedQuery}`,
              { timeoutMs: 20000 }
            );
            if (storedRes.ok) {
              const storedData = await storedRes.json().catch(() => ({}));
              const rawList = storedData.recommended_treatments || [];
              const hasStored = Array.isArray(rawList) && rawList.length > 0;
              if (storedData.success && hasStored) {
                const currentGoals = [];

                if (true) {
                  const list = rawList.slice(0, limit);
                  setRecommendations({
                    ...storedData,
                    recommended_treatments: list,
                    condition_scores:
                      storedData.condition_scores || skinResultsFromState.skinScore.condition_scores || {},
                    generatedWithGoals: currentGoals,
                  });
                  persistRecommendations({
                    ...storedData,
                    recommended_treatments: list,
                    condition_scores:
                      storedData.condition_scores || skinResultsFromState.skinScore.condition_scores || {},
                    generatedWithGoals: currentGoals,
                  });
                  applyCartFromStored(storedData);
                  setIsLoading(false);
                  return;
                }
              }
            }
          } catch (e) {
            console.warn('get-stored-treatments failed', e);
          }
        }

        const skinScore = skinResultsFromState.skinScore;
        const conditionScores = skinScore.condition_scores || {};
        const detectedConditions = (skinResultsFromState.detections || [])
          .map((d) => d.class_name || d.className)
          .filter(Boolean);
        const body = {
          session_id: sessionId,
          condition_scores: conditionScores,
          detected_conditions: detectedConditions,
          app_session_token: token,
          treatment_suggest_limit: limit,
          ...(organizationId ? { organization_id: organizationId } : {}),
        };

        const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-treatments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          timeoutMs: 30000,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || response.statusText || 'Failed to load treatments');
        }
        if (data.success) {
          if (Array.isArray(data.recommended_treatments)) {
            data.recommended_treatments = data.recommended_treatments.slice(0, limit);
          }
          setRecommendations(data);
          persistRecommendations(data);
        } else {
          throw new Error(data.error || 'Failed to get treatment recommendations');
        }
      } catch (err) {
        setError(err.message || 'Failed to load salon treatments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTreatments();
  }, [
    skinResultsFromState,
    API_BASE_URL,
    sessionId,
    treatmentSuggestLimit,
    token,
    organizationId,
    recommendations,
    persistRecommendations,
  ]);

  const renderInlineDetails = (rawText, item, cartKey) => {
    const raw = rawText == null ? '' : String(rawText);
    if (!raw.trim()) {
      return (
        <button
          type="button"
          className="treatment-inline-details-summary treatment-view-details-btn"
          onClick={(e) => openTreatmentDetails(item, cartKey, e)}
        >
          View details
        </button>
      );
    }
    const isHtml = /<[a-z][\s\S]*>/i.test(raw);
    const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
    const plain = stripHtml(raw);
    const preview = plain.length > 140 ? `${plain.slice(0, 140).trim()}…` : plain;
    return (
      <div className="treatment-inline-details">
        <div className="treatment-inline-details-text">{preview}</div>
        <button
          type="button"
          className="treatment-inline-details-summary treatment-view-details-btn"
          onClick={(e) => openTreatmentDetails(item, cartKey, e)}
        >
          View details
        </button>
      </div>
    );
  };

  const handleBack = () => {
    navigate(pathWithSessionId('/product-recommendations', sessionId), {
      state: {
        capturedImage: capturedImageFromState,
        userData: userDataFromState,
        sessionId,
        skinResults: skinResultsFromState,
        routineData: routineDataFromState,
        aqiData: aqiDataFromState,
        treatmentCartItems,
        visibleConditionNames,
        cartItems: productCartItems,
        ...(token && { token }),
      },
    });
  };

  const handleHome = () => {
    navigate(sessionLandingPath);
  };

  const sharedFlowState = {
    capturedImage: capturedImageFromState,
    userData: userDataFromState,
    sessionId,
    skinResults: skinResultsFromState,
    routineData: routineDataFromState,
    aqiData: aqiDataFromState,
    treatmentCartItems,
    visibleConditionNames,
    cartItems: productCartItems,
    ...(token && { token }),
  };

  const handleGoToRecommendedProducts = () => {
    navigate(pathWithSessionId('/product-recommendations', sessionId), { state: sharedFlowState });
  };

  const handleAddToCart = (treatment, quantity, cartKey) => {
    setTreatmentCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[cartKey];
        return next;
      }
      return { ...prev, [cartKey]: { treatment, quantity } };
    });
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalProductCartItems = () =>
    Object.values(productCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalTreatmentCartItems() + getTotalProductCartItems();

  const handleCheckout = () => {
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => setIsCheckoutModalOpen(false);

  const handleTreatmentCartUpdate = (updatedMap) => setTreatmentCartItems(updatedMap || {});
  const handleProductCartUpdate = (updatedMap) => setProductCartItems(updatedMap || {});

  const list = recommendations?.recommended_treatments || [];

  const treatmentUspPopupStyle = (() => {
    const layout = uspPopup.anchorRect ? computeTreatmentUspCardLayout(uspPopup.anchorRect) : null;
    if (!layout) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(90vw, 420px)',
        maxHeight: '85vh',
      };
    }
    return {
      position: 'fixed',
      top: layout.top,
      left: layout.left,
      width: layout.width,
      minHeight: layout.minHeight,
      maxHeight: layout.maxHeight,
      transform: 'none',
    };
  })();

  const formatPrice = (amount) => {
    if (amount == null || amount === '') return '—';
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    return `₹${n.toFixed(0)}`;
  };

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const hasDiscount = (item) => {
    if (toNumberOrNull(item?.net_price) == null) return false;
    const d = item?.discount;
    if (d == null || d === '') return false;
    const n = Number(d);
    return !Number.isNaN(n) && n > 0;
  };

  const hasTreatmentNetPrice = (item) => toNumberOrNull(item?.net_price) != null;
  const hasTreatmentAmount = (item) => toNumberOrNull(item?.amount) != null;

  const formatDiscountBadge = (item) => {
    const d = Number(item?.discount);
    if (Number.isNaN(d) || d <= 0) return null;

    // Heuristics:
    // - 0 < d <= 1: treat as fraction (e.g. 0.15 => 15% OFF)
    // - 1 < d <= 100: treat as percentage
    // - otherwise: treat as currency amount off
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `₹${Math.round(d)} OFF`;
  };

  return (
    <div
      className="product-recommendations-page-shell page-bg treatment-recommendations-page"
      style={{ '--treatment-card-bg-image': `url(${process.env.PUBLIC_URL}/BG/BG.png)` }}
    >
      <div className="product-recommendations-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper">
          <SalonHeaderActions homeButtonClassName="product-recommendations-page-home-button" onHome={handleHome} />
          {getTotalCombinedCartItems() > 0 && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCombinedCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCombinedCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span className="product-recommendations-page-checkout-badge-icon">{getTotalCombinedCartItems()}</span>
            </button>
          )}
        </div>
      </div>

      {!serviceSuggestionEnabled ? (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <div className="product-recommendations-page-error">
              <p>Treatment recommendations are not enabled for this session.</p>
              {productSuggestionEnabled ? (
                <button type="button" className="skin-action-button" onClick={handleGoToRecommendedProducts}>
                  Go to Product Recommendations
                </button>
              ) : (
                <button type="button" className="skin-action-button" onClick={handleBack}>
                  Back
                </button>
              )}
            </div>
          </div>
        </main>
      ) : (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <section className="product-recommendations-page-layout">
              {isLoading ? (
                <div className="product-recommendations-loading">
                  <div className="loading-spinner" />
                  <p>Loading salon treatments…</p>
                </div>
              ) : error ? (
                <div className="product-recommendations-error">
                  <p>{error}</p>
                  <button type="button" onClick={() => window.location.reload()}>
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div className="product-recommendations-content">
                    {list.length > 0 ? (
                    <div className="product-grid">
                      {list.map((item, idx) => {
                        const cartKey = treatmentRowCartKey(item, idx);
                        const key = cartKey;
                        const cartQuantity = treatmentCartItems[cartKey]?.quantity || 0;
                        const showTreatmentPrice = hasTreatmentNetPrice(item);
                        const showTreatmentQuantity = hasTreatmentAmount(item);
                        const sessionsForPrice = showTreatmentQuantity ? Math.max(1, cartQuantity || 0) : 1;
                        const majorUspPlain = item.major_usp ? stripHtml(item.major_usp) : '';
                        const bgPositions = [
                          '8% 0%',
                          '50% 6%',
                          '92% 0%',
                          '15% 55%',
                          '50% 55%',
                          '85% 55%',
                          '25% 100%',
                          '75% 100%',
                        ];
                        const bgPos = bgPositions[idx % bgPositions.length];
                        const bgSizes = ['120% auto', '130% auto', '140% auto', '150% auto'];
                        const bgSize = bgSizes[idx % bgSizes.length];
                        const bgOpacities = [0.18, 0.22, 0.26, 0.2, 0.24, 0.28];
                        const bgOpacity = bgOpacities[idx % bgOpacities.length];
                        const discountBadge = showTreatmentPrice ? formatDiscountBadge(item) : null;

                        return (
                          <div
                            key={key}
                            className="product-card"
                            data-treatment-usp-anchor={cartKey}
                            style={{
                              '--treatment-card-bg-pos': bgPos,
                              '--treatment-card-bg-size': bgSize,
                              '--treatment-card-bg-opacity': bgOpacity,
                            }}
                          >
                            {discountBadge ? (
                              <div className="treatment-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                {discountBadge}
                              </div>
                            ) : null}
                            <div className="product-card-image">
                              {item.image_url ? (
                                <img
                                  src={resolveTreatmentImageUrl(item.image_url)}
                                  alt={item.treatment || item.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : null}
                            </div>
                            <div className="product-info">
                              <div className="product-name-header">
                                <h3 className="product-name">{item.treatment || item.name}</h3>
                              </div>
                              <div className="treatment-inline-details-wrapper">
                                <span className="product-card-usp-heading">Treatment details</span>
                                {renderInlineDetails(
                                  item.description || item.about_treatment || item.major_usp || item.kit,
                                  item,
                                  cartKey,
                                )}
                              </div>
                              {item.major_usp ? (
                                <div className="product-major-usp-wrapper">
                                  <span className="product-card-usp-heading">Key benefit</span>
                                  <span className="product-major-usp-text">{majorUspPlain}</span>
                                </div>
                              ) : null}
                              {item.conditions?.length > 0 && (
                                <div className="product-info-section">
                                  <div className="product-info-label">
                                    <img src={`${process.env.PUBLIC_URL}/icon/treat.png`} alt="" className="product-info-icon" />
                                    <span>Addresses</span>
                                  </div>
                                  <div className="product-tags-container">
                                    {item.conditions.map((c, i) => (
                                      <span key={i} className="product-tag">
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {item.skin_goals?.length > 0 && (
                                <div className="product-info-section">
                                  <div className="product-info-label">
                                    <img src={`${process.env.PUBLIC_URL}/icon/goal.png`} alt="" className="product-info-icon" />
                                    <span>Goals</span>
                                  </div>
                                  <div className="product-tags-container">
                                    {item.skin_goals.map((g, i) => (
                                      <span key={i} className="product-tag">
                                        {g}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {(() => {
                                const brandsRaw = item.brand;
                                const list = Array.isArray(brandsRaw)
                                  ? brandsRaw
                                  : (brandsRaw ? String(brandsRaw).split(',') : []);
                                const brands = list.map((b) => String(b || '').trim()).filter(Boolean);
                                if (!brands.length) return null;
                                return (
                                  <div className="product-info-section">
                                    <div className="product-info-label">
                                      <span>Brand used</span>
                                    </div>
                                    <div className="product-tags-container">
                                      {brands.map((b, i) => (
                                        <span key={`${b}-${i}`} className="product-tag">
                                          {b}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="product-card-footer">
                              {showTreatmentPrice ? (
                                <div className="product-price-quantity">
                                  {(() => {
                                    const unitNet = toNumberOrNull(item.net_price) ?? 0;
                                    const unitSale = toNumberOrNull(item.sale_price) ?? unitNet;
                                    const totalNet = unitNet * sessionsForPrice;
                                    const totalSale = unitSale * sessionsForPrice;
                                    if (hasDiscount(item)) {
                                      return (
                                        <>
                                          <span className="product-price treatment-session-price treatment-net-price-slash">
                                            {formatPrice(totalNet)}
                                          </span>
                                          <span className="product-price treatment-sale-price">
                                            {formatPrice(totalSale)}
                                          </span>
                                        </>
                                      );
                                    }
                                    return (
                                      <span className="product-price treatment-session-price">
                                        {formatPrice(totalNet)}
                                      </span>
                                    );
                                  })()}
                                </div>
                              ) : null}
                              {showTreatmentQuantity ? (
                                <ProductCartCounter
                                  key={`tc-${cartKey}`}
                                  product={item}
                                  onAddToCart={(t, quantity) => handleAddToCart(t, quantity, cartKey)}
                                  disabled={false}
                                  initialQuantity={cartQuantity}
                                  quantityLabel="Sessions"
                                />
                              ) : (
                                <button
                                  type="button"
                                  className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                  onClick={() => handleAddToCart(item, cartQuantity > 0 ? 0 : 1, cartKey)}
                                >
                                  {cartQuantity > 0 ? 'Treatment Requested' : 'Request Treatment'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-products-message">
                      <p>No salon treatments match your current severity and goals. Try adjusting goals or speak with your stylist.</p>
                    </div>
                  )}
                  <div className="treatment-page-cta-row">
                    <button
                      type="button"
                      className="skin-action-button treatment-page-cta-products"
                      onClick={handleGoToRecommendedProducts}
                    >
                      View recommended products
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      )}

      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={productCartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={handleProductCartUpdate}
        onTreatmentCartUpdate={handleTreatmentCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
      />

      {uspPopup.open &&
        createPortal(
          <div className="treatment-about-modal">
            <div
              className="treatment-about-modal__backdrop"
              onClick={handleCloseUspPopup}
              role="presentation"
              aria-hidden="true"
            />
            <div
              className="treatment-about-modal__panel"
              style={treatmentUspPopupStyle}
              role="dialog"
              aria-modal="true"
              aria-labelledby={uspPopup.heading ? 'treatment-about-modal-title' : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="treatment-about-modal__content">
                <button
                  type="button"
                  className="treatment-about-modal__close"
                  onClick={handleCloseUspPopup}
                  aria-label="Close"
                >
                  ×
                </button>
                {uspPopup.heading ? (
                  <h3 className="treatment-about-modal__title" id="treatment-about-modal-title">
                    {uspPopup.heading}
                  </h3>
                ) : null}
                {uspPopup.imageUrl ? (
                  <div className="treatment-about-modal__image">
                    <img
                      src={uspPopup.imageUrl}
                      alt={uspPopup.heading || 'About treatment'}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : null}
                {Array.isArray(uspPopup.sections) && uspPopup.sections.length > 0 ? (
                  <div className="treatment-about-modal__body treatment-about-modal__sections">
                    {uspPopup.sections.map((section) => (
                      <div key={section.label} className="treatment-about-modal__section">
                        <h4 className="treatment-about-modal__section-label">{section.label}</h4>
                        {Array.isArray(section.list) ? (
                          <div className="product-tags-container">
                            {section.list.map((tag) => (
                              <span key={`${section.label}-${tag}`} className="product-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p>{section.value}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  (() => {
                    const raw = String(uspPopup.text || '');
                    const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                    const html = isHtml
                      ? sanitizeHtml(raw)
                      : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                    return <div className="treatment-about-modal__body" dangerouslySetInnerHTML={{ __html: html }} />;
                  })()
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      <PageFooter />
    </div>
  );
};

export default TreatmentRecommendationsPage;
